import { FilePlus2, FileText, History, Send, Undo2 } from 'lucide-react';
import {
  useAuditActivity,
  type AuditActivityItem,
  type AuditActivityKind,
} from '../../../lib/api/queries';

/**
 * Right-rail activity feed for the audit's Report tab. Renders the
 * newest 50 events with an icon, a human-readable line, and a relative
 * timestamp ("just now", "5m ago", "2h ago", or the absolute date once
 * we cross 7 days).
 *
 * Live updates come free — {@link useAuditReportRealtime} already
 * invalidates the activity query when a co-auditor saves a section or
 * captures a snapshot.
 */
export function ActivityFeed({
  tenantId,
  auditId,
}: {
  tenantId: string;
  auditId: string;
}) {
  const { data, isLoading, error } = useAuditActivity(tenantId, auditId);

  return (
    <aside className="border-l border-gray-200 pl-3 py-3 w-44 shrink-0
                      hidden lg:block">
      <header className="flex items-center gap-1.5 mb-3">
        <History className="w-4 h-4 text-gray-500" aria-hidden />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Activity
        </h3>
      </header>

      {isLoading && (
        <p className="text-xs text-gray-400">Loading…</p>
      )}
      {error && (
        <p className="text-xs text-danger-500">
          Couldn't load activity — try refreshing.
        </p>
      )}
      {data && data.length === 0 && (
        <p className="text-xs text-gray-400">
          No activity yet. Save a section or capture a version to start
          the log.
        </p>
      )}

      {data && data.length > 0 && (
        <ol className="space-y-3">
          {data.map(item => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </ol>
      )}
    </aside>
  );
}

function ActivityRow({ item }: { item: AuditActivityItem }) {
  const Icon = iconFor(item.kind);
  const description = describe(item);
  return (
    <li className="flex gap-2">
      <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center
                      justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-gray-600" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-800 leading-snug">
          <span className="font-medium">
            {item.actorDisplayName ?? 'Unknown user'}
          </span>{' '}
          {description}
        </p>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {formatWhen(item.createdAtUtc)}
        </p>
      </div>
    </li>
  );
}

function iconFor(kind: AuditActivityKind) {
  switch (kind) {
    case 'AuditCreated':          return FilePlus2;
    case 'ReportSectionSaved':    return FileText;
    case 'ReportSnapshotCreated': return History;
    case 'ReportPublished':       return Send;
    case 'ReportUnpublished':     return Undo2;
    // Fallback icon — protects the SPA from crashing if the server ships
    // a new kind before the SPA is updated. Rare but not impossible.
    default:                      return FileText;
  }
}

function describe(item: AuditActivityItem): string {
  const payload = parsePayload(item.payloadJson);
  switch (item.kind) {
    case 'AuditCreated':
      return `created this audit${
        payload?.title ? ` — "${payload.title}"` : ''
      }.`;
    case 'ReportSectionSaved': {
      const title = payload?.sectionTitle ?? 'a section';
      const rev = payload?.revision;
      return `saved ${title}${rev ? ` (v${rev})` : ''}.`;
    }
    case 'ReportSnapshotCreated':
      return `captured a version${
        payload?.label ? ` — "${payload.label}"` : ''
      }.`;
    case 'ReportPublished':
      return `published a version to the client${
        payload?.label ? ` — "${payload.label}"` : ''
      }.`;
    case 'ReportUnpublished':
      return `retracted a version${
        payload?.label ? ` — "${payload.label}"` : ''
      }.`;
    default:
      return 'did something on this audit.';
  }
}

type Payload = {
  title?: string;
  sectionKey?: string;
  sectionTitle?: string;
  revision?: number;
  snapshotId?: string;
  label?: string | null;
  sectionCount?: number;
};

function parsePayload(json: string | null): Payload | null {
  if (!json) return null;
  try { return JSON.parse(json) as Payload; }
  catch { return null; }
}

/**
 * Relative-time formatter tuned to feed brevity:
 *   < 1 min   →  "just now"
 *   < 1 hr    →  "5m ago"
 *   < 1 day   →  "2h ago"
 *   < 7 days  →  "3d ago"
 *   older     →  the absolute date "6 Jul 2026"
 */
function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSeconds = Math.max(0, Math.round((now - then) / 1000));

  if (diffSeconds < 60)  return 'just now';
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60)  return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24)    return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7)      return `${diffDays}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}
